import React, { useState } from 'react'
import { Button, TextField } from '@ui'
import { UserTable } from '../components/UserTable.jsx'
import { InviteModal } from '../components/InviteModal.jsx'
import { BulkImportModal } from '../components/BulkImportModal.jsx'
import { UserDetailPanel } from './UserDetailPanel.jsx'
import { useAdminUsers } from '../hooks/useAdminUsers.js'

const STATUS_FILTERS = ['all', 'active', 'inactive', 'orphaned', 'invited', 'deactivated']

export function UsersPage() {
  const [search, setSearch] = useState('')
  const [debouncedSearch, setDebouncedSearch] = useState('')
  const [status, setStatus] = useState('all')
  const [page, setPage] = useState(1)
  const [showInvite, setShowInvite] = useState(false)
  const [showBulk, setShowBulk] = useState(false)
  const [selectedUser, setSelectedUser] = useState(null)
  const { users, total, loading, inviteUser, bulkInvite, deactivateUser, reactivateUser, resendInvite } = useAdminUsers({
    search: debouncedSearch,
    status,
    page,
  })

  function handleSearchChange(e) {
    setSearch(e.target.value)
    clearTimeout(window._adminSearchTimer)
    window._adminSearchTimer = setTimeout(() => {
      setDebouncedSearch(e.target.value)
      setPage(1)
    }, 300)
  }

  const LIMIT = 50
  const totalPages = Math.ceil(total / LIMIT)

  return (
    <div>
      <div style={styles.header}>
        <div>
          <h2 style={styles.title}>Users</h2>
          <p style={styles.sub}>{total} total users</p>
        </div>
        <div style={styles.actions}>
          <Button onClick={() => setShowInvite(true)}>+ Invite user</Button>
          <Button variant="secondary" onClick={() => setShowBulk(true)}>Bulk import CSV</Button>
        </div>
      </div>

      <div style={styles.filters}>
        <div style={{ width: 280 }}>
          <TextField
            type="search"
            placeholder="Search users…"
            value={search}
            onChange={handleSearchChange}
            aria-label="Search users"
          />
        </div>
        <div style={styles.statusFilters}>
          {STATUS_FILTERS.map((s) => (
            <button
              key={s}
              style={{ ...styles.filterBtn, ...(status === s ? styles.filterActive : {}) }}
              onClick={() => { setStatus(s); setPage(1) }}
            >
              {s === 'all' ? 'All' : s.charAt(0).toUpperCase() + s.slice(1)}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div style={styles.loading}>Loading…</div>
      ) : (
        <>
          <UserTable
            users={users}
            onRowClick={(u) => setSelectedUser(u.userId)}
          />
          {totalPages > 1 && (
            <div style={styles.pagination}>
              <button style={styles.pageBtn} disabled={page === 1} onClick={() => setPage(page - 1)}>‹</button>
              <span style={styles.pageInfo}>{page} / {totalPages}</span>
              <button style={styles.pageBtn} disabled={page === totalPages} onClick={() => setPage(page + 1)}>›</button>
            </div>
          )}
        </>
      )}

      {showInvite && (
        <InviteModal onClose={() => setShowInvite(false)} onSubmit={inviteUser} />
      )}
      {showBulk && (
        <BulkImportModal onClose={() => setShowBulk(false)} onSubmit={bulkInvite} />
      )}
      {selectedUser && (
        <UserDetailPanel
          userId={selectedUser}
          onClose={() => setSelectedUser(null)}
          onDeactivate={deactivateUser}
          onReactivate={reactivateUser}
          onResendInvite={resendInvite}
        />
      )}
    </div>
  )
}

const styles = {
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20 },
  title: { fontSize: 20, fontWeight: 600, marginBottom: 2 },
  sub: { fontSize: 12, color: 'var(--text-secondary)' },
  actions: { display: 'flex', gap: 8 },
  filters: { display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 16 },
  statusFilters: { display: 'flex', gap: 4 },
  filterBtn: { padding: '4px 12px', borderRadius: 20, border: '1px solid var(--accent-brand)', background: 'none', fontSize: 12, cursor: 'pointer', color: 'var(--accent-brand)' },
  filterActive: { background: 'var(--accent-brand)', color: '#fff', borderColor: 'var(--accent-brand)' },
  loading: { fontSize: 13, color: 'var(--text-secondary)', padding: 32 },
  pagination: { display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 12, marginTop: 16 },
  pageBtn: { padding: '4px 10px', background: 'none', border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)', cursor: 'pointer', fontSize: 14 },
  pageInfo: { fontSize: 12, color: 'var(--text-secondary)' },
}
